import apiClient from "../../api/api";

/**
 * Retrieves the trolley payroll report for a given date range and pay frequency.
 *
 * @async
 * @function getTrolleyPayrollRequest
 * @param {string} from - Start date of the payroll period (format: YYYY-MM-DD).
 * @param {string} to - End date of the payroll period (format: YYYY-MM-DD).
 * @param {string} payFrequency - Pay frequency used for the report (e.g. "weekly", "biweekly", "monthly").
 * @returns {Promise<Object>} Trolley payroll report data returned by the API.
 *
 * @example
 * await getTrolleyPayrollRequest("2025-12-01", "2025-12-31", "monthly");
 */
export const getTrolleyPayrollRequest = async(from, to, payFrequency, pageNumber = 1, limit = 1000) => {
    const getTrolleyPayrollResponse = await apiClient.get(import.meta.env.VITE_GET_TROLLEY_REPORT+`?from=${from}&to=${to}&payFrequency=${payFrequency}&page=${pageNumber}&limit=${limit}`, {timeout: 180000} )
    
    return getTrolleyPayrollResponse.data
}

/**
 * Retrieves a summarized view of a user's time and payroll data for a given period.
 *
 * @async
 * @function getUserSummaryRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {string} from - Start date of the summary period (format: YYYY-MM-DD).
 * @param {string} to - End date of the summary period (format: YYYY-MM-DD).
 * @returns {Promise<Object>} User summary data returned by the API.
 *
 * @example
 * await getUserSummaryRequest(1097, "2025-12-01", "2025-12-31");
 */
export const getUserSummaryRequest = async(userId, from, to) => {
    const getUserSummaryResponse = await apiClient.get(import.meta.env.VITE_GET_USER_SUMMARY+`/${userId}/summary?from=${from}&to=${to}`)
    
    return getUserSummaryResponse.data
}

/**
 * Retrieves payroll details grouped by project.
 *
 * @async
 * @function getPayrollByProjectListRequest
 * @param {Object} payrollDetail - Payload containing payroll filter and grouping criteria.
 * @returns {Promise<Object>} Payroll data grouped by project.
 *
 * @example
 * await getPayrollByProjectListRequest({ from: "2025-12-01", to: "2025-12-31" });
 */
export const getPayrollByProjectListRequest = async(from, to, projectIds) => {
    const params = new URLSearchParams();
    params.append("from", from);
    params.append("to", to);

    projectIds.forEach((id) => {
        params.append("projectIds", id);
    });
    const getPayrollResponse = await apiClient.get(import.meta.env.VITE_REPORTS_PAYROLL_URL, { params })
    return getPayrollResponse.data
}

/**
 * Retrieves a user's weekly payroll data for a specific project and date range.
 *
 * @async
 * @function getUserWeeklyPayrollRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {number|string} projectId - Unique identifier of the project.
 * @param {string} from - Start date of the payroll period (format: YYYY-MM-DD).
 * @param {string} to - End date of the payroll period (format: YYYY-MM-DD).
 * @returns {Promise<Object>} Weekly payroll data returned by the API.
 *
 * @example
 * await getUserWeeklyPayrollRequest(1097, 16, "2025-12-01", "2025-12-07");
 */
export const getUserWeeklyPayrollRequest = async(userId, projectId, from, to) => {
     const getUserWeeklyPayrollResponse = await apiClient.get(import.meta.env.VITE_GET_USER_SUMMARY+`/${userId}/payroll/weekly/projects/${projectId}?from=${from}&to=${to}`)
    
    return getUserWeeklyPayrollResponse.data
}

/**
 * Retrieves a user's daily payroll data for a specific project and date.
 *
 * @async
 * @function getUserDailyPayrollRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {number|string} projectId - Unique identifier of the project.
 * @param {string} from - Date of the payroll entry (format: YYYY-MM-DD).
 * @returns {Promise<Object>} Daily payroll data returned by the API.
 *
 * @example
 * await getUserDailyPayrollRequest(1097, 16, "2025-12-15");
 */
export const getUserDailyPayrollRequest = async(userId, projectId, from) => {
    const getPayrollResponse = await apiClient.get(import.meta.env.VITE_GET_USER_SUMMARY+`/${userId}/payroll/daily/projects/${projectId}?from=${from}`)
    
    return getPayrollResponse.data
}

/**
 * Retrieves the weekly payroll report for projects, optionally filtered by project name.
 *
 * @async
 * @function getProjectPayrollWeekly
 * @param {string} from - Start date of the payroll period (format: YYYY-MM-DD).
 * @param {string} to - End date of the payroll period (format: YYYY-MM-DD).
 * @param {string} projectName - Project name used to filter the report.
 * @returns {Promise<Array<Object>>} Weekly payroll data grouped by project.
 *
 * @example
 * await getProjectPayrollWeekly("2025-01-01", "2025-01-07", "Customer Support");
 */
export const getProjectPayrollWeekly = async(from, to, projectName) => {
    const params = {
        from,
        to,
        projectName
    }
    const getPayrollResponse = await apiClient.get(import.meta.env.VITE_GET_REPORTS_PROJECT_WEEKLY_PAYROLL, {params})
    
    return getPayrollResponse.data
}

/**
 * Retrieves payroll data for a specific user and project within a given period.
 *
 * @async
 * @function getUserPayrollFromPeriod
 * @param {number|string} userId - Unique identifier of the user.
 * @param {number|string} projectId - Unique identifier of the project.
 * @param {string} from - Start date of the payroll period (format: YYYY-MM-DD).
 * @param {string} to - End date of the payroll period (format: YYYY-MM-DD).
 * @returns {Promise<Object>} Payroll data returned by the API for the given period.
 *
 * @example
 * await getUserPayrollFromPeriod(1097, 16, "2025-01-01", "2025-01-31");
 */
export const getUserPayrollFromPeriod = async(userId, projectId, from, to) => {
    const params = {
        from,
        to
    }
    const getPayrollFromPeriodResponse = await apiClient.get(import.meta.env.VITE_GET_USER_SUMMARY+`/${userId}/payroll/projects/${projectId}`, { params })
    
    return getPayrollFromPeriodResponse.data
}